import { Check } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { useSession } from '@/features/auth/api/useSession'
import { refusalMessage } from '@/features/cases/lib/refusal'
import {
  toAllocationPayload,
  useAllocatePayment,
  useConfirmPayment,
} from '@/features/finance/api/useFinance'
import { allocationLabel, isReceivableAllocation } from '@/features/finance/allocationLabel'
import { ClientPaymentSheet } from '@/features/finance/components/ClientPaymentSheet'
import { PaymentAllocationSheet } from '@/features/finance/components/PaymentAllocationSheet'
import { PaymentAmendSheet } from '@/features/finance/components/PaymentAmendSheet'
import { RejectPaymentSheet } from '@/features/finance/components/RejectPaymentSheet'
import { ReverseAllocationDialog } from '@/features/finance/components/ReverseAllocationDialog'
import { PaymentStatusTag } from '@/features/finance/components/PaymentStatusTag'
import { useAllocationTargets } from '@/features/finance/components/useAllocationTargets'
import { paymentRefusalMessage } from '@/features/finance/paymentRefusal'
import { useFormat } from '@/i18n/useFormat'
import { CAP, can } from '@/lib/can'
import { cn } from '@/lib/cn'
import { formatMoney, formatNumber } from '@/lib/format'
import type { Payment } from '@/types/domain'

/* What one payment has been applied to, the act of applying it, and — since
 * 0059 — Finance's second pair of eyes on the record.
 *
 * The rows come from the PAYMENT the caller passes, which is the row the
 * server most recently confirmed — never from local state and never from the
 * mutation's own variables. That is the whole of UX-DR-24 here: allocating
 * invalidates the payment queries, the list refetches, and the new row appears
 * because the server said it exists. While the request is in flight the list
 * below is exactly what it was, and a refusal leaves it untouched with the
 * server's message on the sheet.
 *
 * A payment with nothing applied to it is not an error state and is not styled
 * as one: unallocated cash is legal (AD-11). It says what it is and offers the
 * act that would change it.
 *
 * ── The targets depend on the DIRECTION ─────────────────────────────────────
 *
 * `out` settles this case's ACTUAL cost lines, in `PaymentAllocationSheet`
 * over the targets `useAllocationTargets` resolves out of the case the payment
 * names. `in` settles the case's receivables — its issued invoices, its
 * client's issued debit notes and the tranches no invoice has replaced — in
 * the client payment sheet's allocate mode, which reads what each still takes
 * (S-21). The tranche-only picker that stood here, beside a sentence saying
 * invoices and debit notes could not be settled yet, is gone.
 *
 * ── Confirm and reject, and what neither of them does to the money ──────────
 *
 * A payment recorded by Operations is born `pending` and counts in every figure
 * from that instant (spec L4): «Оплачено», the balances, the aging. Confirming
 * changes the RECORD's status and moves no sum, and rejecting DELETES the row
 * with a mandatory reason rather than marking it rejected — a rejected row left
 * on the ledger is a figure every sum then has to remember to exclude (L8).
 *
 * ── Who is offered which act: the server's verdicts (S-21) ──────────────────
 *
 * «Разнести», «Отменить разнесение», «Исправить» and «Отклонить» / «Удалить»
 * follow the payment's served booleans (`canAllocate`,
 * `canReverseAllocations`, `canAmend`, `canDelete`) and nothing else. The
 * server computes them with the predicates its writes ask, so the recorder of
 * a pending client payment (Sales) is offered their own acts and nobody
 * else's, without the wire ever saying who recorded it — a `can(user, …)`
 * here could not know. «Подтвердить» has no verdict and stays on
 * `finance.payment.confirm`. Each act is ABSENT, not refused, when its verdict
 * is no; the server re-checks every one (AD-21).
 */

/* One row of the settlement list, and the legend above it. Stacked in a narrow
   container and four aligned columns once there is room — see the `@container`
   note in the render. Written once so the legend cannot drift from the rows it
   heads. */
const ROW =
  'border-line-hair grid grid-cols-1 gap-x-4 gap-y-0.5 border-b py-1.5 last:border-b-0 ' +
  '@2xl:grid-cols-[minmax(0,1fr)_8rem_13rem_8rem] @2xl:items-baseline'

export function PaymentAllocations({
  payment,
  onServerRow,
}: {
  payment: Payment
  /** Handed the server's row after EVERY write from this block that answers
   *  with one — the confirm and the allocation. The Money page's sheet keeps
   *  it, because in the pending queue a confirmed payment leaves the list the
   *  sheet is drawn from, and from then on the kept row is the only copy that
   *  can follow an allocation made in the same sitting. (A rejection answers
   *  204 and deletes the row; there is nothing to hand.) */
  onServerRow?: (row: Payment) => void
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const { data: user } = useSession()
  const allocate = useAllocatePayment()
  const confirm = useConfirmPayment()
  const [open, setOpen] = useState<null | 'allocate' | 'reverse' | 'amend' | 'delete'>(null)

  const currency = payment.amount.currency
  const inbound = payment.direction === 'in'

  /* The server's verdict (S-21): a `finance.payment.record` holder, or the
     recorder of this pending client payment. Absent, not refused, without it
     (QW-7): `allocateBlocked` below is for the PAYMENT's states, which a
     reader who may allocate can do something about. */
  const mayAllocate = payment.canAllocate

  /* What an OUTBOUND payment may settle and why the act is refused when it is
     (useAllocationTargets, which the supplier payments' row acts read too); an
     inbound one is refused only once it is fully allocated. */
  const { targets, blocked: allocateBlocked } = useAllocationTargets(payment, mayAllocate)

  /* Reversing is for money RECEIVED, and only an allocation of a receivable
     can be reversed — so the act needs one to offer. */
  const mayReverse =
    payment.canReverseAllocations && payment.allocations.some(isReceivableAllocation)

  const pending = payment.status === 'pending'
  const mayConfirm = can(user, CAP.financePaymentConfirm)
  const confirmedHere = confirm.isSuccess && confirm.variables === payment.id

  return (
    /* A CONTAINER query, not a viewport one, and it is the only way this block
       can be right in both of its homes: it is a full-width band under a
       payment row on the case's money card, and a ~450px column inside the
       Finance page's sheet. A `sm:` here would give the sheet four columns it
       cannot hold, because the viewport is wide either way. */
    <div className="@container flex flex-col gap-2.5 py-1">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <span className="asa-eyebrow">{t('finance.allocations.heading')}</span>
          <PaymentStatusTag status={payment.status} />
        </span>

        <span className="flex flex-wrap items-center gap-2">
          {/* Finance's second pair of eyes, offered only on a record that is
              still waiting and only to a reader who holds the capability:
              confirming twice is a no-op the server absorbs. */}
          {pending && mayConfirm ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={confirm.isPending}
              onClick={() =>
                confirm.mutate(payment.id, {
                  onSuccess: (confirmed) => onServerRow?.(confirmed),
                })
              }
            >
              {t('finance.payments.confirm')}
            </Button>
          ) : null}

          {payment.canAmend ? (
            <Button variant="secondary" size="sm" onClick={() => setOpen('amend')}>
              {t('finance.payments.amend')}
            </Button>
          ) : null}

          {/* One deletion with a reason, worded by who decides it: Finance
              REJECTS somebody's record, the recorder of a pending client
              payment DELETES their own. A confirmed payment is deleted by
              nobody (`ErrPaymentNotPending`), and its verdict says so. */}
          {payment.canDelete ? (
            <Button variant="secondary" size="sm" onClick={() => setOpen('delete')}>
              {mayConfirm ? t('finance.payments.reject') : t('finance.payments.delete')}
            </Button>
          ) : null}

          {mayReverse ? (
            <Button variant="secondary" size="sm" onClick={() => setOpen('reverse')}>
              {t('finance.reverse.action')}
            </Button>
          ) : null}

          {mayAllocate ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setOpen('allocate')}
              blockedReason={allocateBlocked}
            >
              {t('finance.allocations.allocate')}
            </Button>
          ) : null}
        </span>
      </div>

      {/* A refused confirm has nowhere else to go — there is no sheet holding a
          draft — so it is said here, in the reader's own language. */}
      {confirm.error ? (
        <p className="text-negative text-[12.5px] leading-snug">
          {refusalMessage(confirm.error, t)}
        </p>
      ) : null}

      {/* The visible answer to a successful confirm (QW-14). The refetch takes
          both buttons and the pending tag away at once, which on its own reads
          as "nothing happened". Inline, in both hosts, rather than a toast: one
          host is a sheet, and a toast over a sheet is a layering question this
          line does not have to answer.

          Keyed on the id the confirm was SENT for, not on "a confirm
          succeeded": the Money page's sheet keeps this block mounted and hands
          it the next row, and the mutation outlives the switch — a bare
          `isSuccess` would follow the reader onto a payment nobody confirmed. */}
      {confirmedHere ? (
        <p
          role="status"
          className="text-positive flex items-center gap-1.5 text-[12.5px] leading-snug"
        >
          <Check aria-hidden className="size-3.5 shrink-0" />
          {t('finance.payments.confirmed')}
        </p>
      ) : null}

      {payment.allocations.length === 0 ? (
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('finance.allocations.none')}
        </p>
      ) : (
        <>
        {/* The column heading, and it earns its line on «по 7.250000 · 2 500.00
            USD» alone: the settlement booking read as a bare fragment beside two
            other numbers is the one cell on this row nobody could name. Its own
            element rather than a first `<li>`, so the list holds settlements
            and nothing else; hidden where the columns are stacked and each row
            is read down instead of across. */}
        <div className={`${ROW} text-text-muted hidden text-[10px] font-medium tracking-wide uppercase @2xl:grid`}>
          <span>{t('finance.allocations.columns.target')}</span>
          <span className="text-right">{t('finance.allocations.columns.amount')}</span>
          <span className="text-right">{t('finance.allocations.columns.booked')}</span>
          <span className="text-right">{t('finance.allocations.columns.at')}</span>
        </div>

        <ul className="flex flex-col">
          {payment.allocations.map((allocation) => {
            // Signed, and the sign is the information: a settlement worth less
            // in USD than the figure the target was booked at is a loss on the
            // rate, and it is recorded rather than smoothed away (AD-9).
            const difference = Number(allocation.fxSettlementDifference)
            return (
              <li key={allocation.id} className={ROW}>
                {/* In the reader's words from the target's facts — «Счёт
                    AG-INV-17», «Транш 2 из 2» — and the server's label only
                    for a cost line or a row served without them. */}
                <span className="text-text-body text-[13px] [overflow-wrap:anywhere]">
                  {allocationLabel(t, allocation)}
                </span>
                <span className="asa-num text-text-heading text-[13px] @2xl:text-right">
                  {formatMoney(allocation.amount.amount, allocation.amount.currency)}
                </span>
                {/* The settlement booking, not the target's. The two differ by
                    exactly the figure beside them, and the target's own booked
                    USD is never rewritten to close the gap. The difference
                    travels in THIS cell rather than in one of its own: a column
                    that is empty on every allocation but the odd one is a
                    column of nothing, and the two facts are one sentence about
                    the rate.
                    A USD allocation drops the rate clause, as the payment does
                    (QW-6, F-2): the server's rate is full precision, so a USD
                    booking arrives as "1", and «по 1» restated the currency as
                    a conversion that never happened. The USD figure then
                    repeats the amount beside it, and that is accepted: the
                    column still says what the target was booked at. The branch
                    compares the currency CODE; nothing is computed. */}
                <span className="asa-num text-text-muted text-[12px] @2xl:text-right">
                  {allocation.amount.currency === 'USD'
                    ? formatMoney(allocation.usd, 'USD')
                    : t('finance.allocations.settledAt', {
                        rate: formatNumber(allocation.appliedRate, 6),
                        usd: formatNumber(allocation.usd, 2),
                      })}
                  {difference === 0 ? null : (
                    <span
                      className={cn(
                        'ml-1.5 font-medium',
                        difference < 0 ? 'text-negative' : 'text-positive',
                      )}
                      title={t('finance.allocations.fxDifferenceHint')}
                    >
                      {t('finance.allocations.fxDifference', {
                        value: formatNumber(allocation.fxSettlementDifference, 4),
                      })}
                    </span>
                  )}
                </span>
                <span className="text-text-muted text-[12px] font-light whitespace-nowrap @2xl:text-right">
                  {format.timestamp(allocation.allocatedAt)}
                </span>
              </li>
            )
          })}
        </ul>
        </>
      )}

      {/* Closed from onSuccess only, like every other write on this page: a
          refusal leaves the sheet open holding the splits rather than closing
          over an allocation that never landed. Mounted only for a reader who may
          allocate, like the button that opens it. Money RECEIVED goes to the
          client payment sheet, which reads the case's receivables and owns its
          own request; money paid out to its cost lines here. */}
      {mayAllocate && inbound ? (
        <ClientPaymentSheet
          mode="allocate"
          open={open === 'allocate'}
          onClose={() => setOpen(null)}
          payment={payment}
          onAllocated={(allocated) => onServerRow?.(allocated)}
        />
      ) : null}
      {mayAllocate && targets ? (
        <PaymentAllocationSheet
          open={open === 'allocate'}
          onClose={() => setOpen(null)}
          payment={payment}
          pending={allocate.isPending}
          /* Worded here, never `error.message` — the problem's `detail`,
             English written for a log (AD-27). */
          submitError={allocate.isError ? paymentRefusalMessage(allocate.error, t) : undefined}
          targets={targets}
          onSubmit={(splits) =>
            allocate.mutate(
              { paymentId: payment.id, payload: toAllocationPayload(splits, currency) },
              {
                onSuccess: (allocated) => {
                  setOpen(null)
                  onServerRow?.(allocated)
                },
              },
            )
          }
        />
      ) : null}

      {mayReverse ? (
        <ReverseAllocationDialog
          open={open === 'reverse'}
          onClose={() => setOpen(null)}
          payment={payment}
          onReversed={(reversed) => onServerRow?.(reversed)}
        />
      ) : null}

      {payment.canAmend ? (
        <PaymentAmendSheet
          open={open === 'amend'}
          onClose={() => setOpen(null)}
          payment={payment}
        />
      ) : null}

      {/* The reason sheet, shared with the direction page's settlements table
          so the wording and the mandatory-reason rule cannot drift between the
          places the act is offered. */}
      {payment.canDelete ? (
        <RejectPaymentSheet
          open={open === 'delete'}
          onClose={() => setOpen(null)}
          payment={payment}
          verb={mayConfirm ? 'reject' : 'delete'}
        />
      ) : null}
    </div>
  )
}
